<template lang="pug">
Modal(v-if="showConfirm" )
  .modal-body(v-if="data")
    .title {{__('Confirm your region')}}
    .text {{__('It looks like you\'re in {country}. Please confirm your region to correctly display pricing and availability.', {country: data.name})}}
    .opts
      RogueButton(color="black" @click="goTo()")
        .btn-contents
          Icon(:name="'flags/'+data.country.toUpperCase()")
          .countryname {{ data.name }}
      RogueButton(color="gray" @click="hide(true)")
        .btn-contents
          Icon(:name="'flags/'+data.current.toUpperCase()")
          .countryname {{ data.currentName }}
</template>
<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useRogue } from '@/js/services/Rogue'
import { getCookie, setCookie } from '@/js/services/Utils'

type GeoData = {
  country: string,
  path: string,
  name?: string,
  current?: string | string[],
  currentName?: string
}

const COOKIE_NAME = 'preferredCountry'

const showConfirm = ref(false)
const data = ref<GeoData>()

const rogue = useRogue()
const route = useRoute()

const emit = defineEmits(['input'])

function hide(sendEvent = false) {
  showConfirm.value = false

  setCookie(COOKIE_NAME, rogue.l10n.country, 365)
  
  if (sendEvent) {
    rogue.track.track({ type: 'custom', event: 'ipDetectionCancel', data: { selected: data.value?.current, ipLocation: data.value?.country } })
  }
  emit('input', false)
}

async function goTo() {
  const isLocalhost = window.location.hostname === 'localhost'
  const newUrl = new URL(`${isLocalhost ? window.location.origin : 'https://www.roguefitness.com'}${route.fullPath}`)
  // We want to go to the same page - but on the correct site

  newUrl.searchParams.delete('_forcegeo')

  const [dataPath, sdst] = data.value!.path.split('?_sdst=')

  // Some EU countries do not get their own path variables
  if (sdst) {
    newUrl.searchParams.delete('_sdst')
    newUrl.searchParams.set('_sdst', sdst.replace('%2F', ''))
  }

  // Site roots always end in a trailing slash; deep pages never do
  newUrl.pathname = newUrl.pathname === '/' ? `${dataPath}/` : `${dataPath}${newUrl.pathname}`

  // If we are on US, and going to EU, we need to find the correct route to go to
  if (!['EUR', 'GBR'].includes(rogue.site.code)) {
    if (route.fullPath.startsWith('/invitational') || route.fullPath.startsWith('/events') || route.fullPath.startsWith('/theindex')) {
      newUrl.pathname = `${dataPath}${route.path}`
    } else {
      try {
        const routeData = await rogue.fetch<{ related?: { [siteKey: string]: string } }>(`/svc_route/route?url=${route.path}`)
        let euPath = routeData?.related?.EUROPE
        if (euPath) {
          if (euPath === '/') {
            euPath = ''
          } else if (euPath.charAt(0) !== '') {
            euPath = `/${euPath}`
          }
          newUrl.pathname = `${dataPath}${euPath || '/'}`
        }
      } catch (e: any) {
        rogue.log.warn(`Not able to find route for ${route.path}`, e)
        newUrl.pathname = `${dataPath}/`
      }
    }
  }

  if (!isLocalhost && window.location.hostname.indexOf('www.') === -1) {
    newUrl.hostname = newUrl.hostname.replace('www', window.location.hostname.split('.')[0])
  }

  rogue.track.track({
    type  : 'custom',
    event : 'ipDetectionConfirm',
    data  : {
      toCountry   : data.value?.country,
      fromCountry : rogue.l10n.country,
      to          : newUrl.toString(),
      from        : window.location.toString(),
    },
  })
  // Set the cookie so they wont see the popup again
  setCookie(COOKIE_NAME, data.value!.country, 365)

  setTimeout(() => window.location.href = newUrl.toString(), 50)
}

onMounted(() => {
  const force = route.query._forcegeo ? `&_force=${route.query._forcegeo}` : ''

  const cookie = getCookie(COOKIE_NAME)?.toLowerCase()
  // If we are in the country that we have a cookie to be in, dont show the modal.
  // Also early-exit on path /eu without _sdst — the server always defaults to
  // Belgium there, so cookie === rogue.l10n.country would never match a specific
  // preferred country even though the user already has a preference set.
  const onEuWithoutSdst = rogue.l10n.path.startsWith('/eu') && !route.query._sdst && !route.query._forcegeo
  if (cookie && (cookie === rogue.l10n.country.toLowerCase() || onEuWithoutSdst)) {
    rogue.log.info(`Already on preferred country ${rogue.l10n.country}`)
    emit('input', false)

    return
  }

  const effectiveCurrent = (cookie && rogue.l10n.path.startsWith('/eu') && !route.query._sdst)
    ? cookie.toUpperCase()
    : rogue.l10n.country
  rogue.fetch<GeoData>(`/svc_ssr/geoIp?current=${effectiveCurrent}${force}`).then(resp => {
    rogue.log.info(`Customer in ${resp.country} browsing ${rogue.l10n.country}`)
    data.value = resp
    const path = resp.path?.split('?')[0].toLowerCase()
    rogue.track.track({ type: 'custom', event: 'ipDetection', data: { site: rogue.site.code, ipCountry: resp.country } })

    if (!path || path === '/au' || path === '/ca') {
      // Dont redirect to AU or CA or US
      hide()
    } else if (resp.country.toLowerCase() === rogue.l10n.country.toLowerCase()) {
      // Dont redirect if the country is the same as what we are currently in
      hide()
    } else if (cookie && cookie === resp.country.toLowerCase()) {
      goTo()
    } else {
      showConfirm.value = true
    }
  }).catch(() => ({
    //
  }))
})
</script>
<style lang="scss" scoped>
.modal-body {
  padding: 0 $space-s;
  text-align: center;
}

.title {
  font-size: $size-xl;
  font-weight: $weight-bold;
  text-transform: uppercase;
  font-family: $font-header;
  margin-bottom: $space-m;
}

.btn-contents, .opts {
  display: flex;
  justify-content: center;
  align-items: center;
}

.btn-contents .countryname {
  margin-left: $space-xxs;
}

.opts {
  margin-top: $space-m;

  > * {
    flex-grow: 1;
    flex-shrink: 0;
    width: 50%;

    &:first-child {
      margin-right: $space-s;
    }
  }
}
</style>
